import { useState, useEffect, useRef } from "react";
import "./App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import CustomCaptcha from './components/CustomCaptcha';
import AudioRecorder from './components/AudioRecorder';
import MentalHealthIndicator from './components/MentalHealthIndicator';
// Iconos
import { Plus, Send, Trash2, AlertTriangle, Brain, Tag, Lock, LogOut, BarChart3,FileDown,  Users, User, EyeOff, MapPin, Menu, X } from "lucide-react";

// Componentes de la UI (shadcn/ui)
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";

// 🆕 IMPORTAR HOOK DE UBICACIÓN
import { useLocation } from "./hooks/useLocation";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL 
const API = `${BACKEND_URL}/api`;

// --- SIMULACIÓN DE AUTH Y GESTIÓN DE ID ---
const SIMULATED_USER_ID = "user-123";

const getUserIdOrSessionId = (isLoggedIn) => {
  if (isLoggedIn) {
    return localStorage.getItem('emoanalyzer_user_id') || SIMULATED_USER_ID;
  }
  let sessionId = sessionStorage.getItem('emoanalyzer_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('emoanalyzer_session_id', sessionId);
  }
  return sessionId;
};

const cleanupAnonymousSession = async (isLoggedIn) => {
  if (isLoggedIn) {
    console.log('👤 Usuario logeado - conversaciones preservadas');
    return;
  }

  try {
    const sessionId = sessionStorage.getItem('emoanalyzer_session_id');
    if (!sessionId) return;

    const url = `${API}/conversations/cleanup-on-close`;
    const blob = new Blob(
      [JSON.stringify({})],
      { type: 'application/json' }
    );
    const urlWithParams = `${url}?session_id=${encodeURIComponent(sessionId)}`;
    const beaconSent = navigator.sendBeacon(urlWithParams, blob);

    if (!beaconSent) {
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
        body: JSON.stringify({}),
        keepalive: true
      });
    }
    console.log('🗑️ Limpieza de cliente iniciada');
  } catch (error) {
    console.error('Error en limpieza automática:', error);
  }
};


// ---- PASO 2: Nueva función para descargar el informe ----
// Añade esta función NUEVA justo después de cleanupAnonymousSession:

const downloadMentalHealthReport = async (conversationId, sessionId, isLoggedIn) => {
  if (!conversationId) {
    console.log('⚠️ Sin conversación activa, no se genera informe');
    return;
  }

  try {
    console.log('📋 Generando informe de salud mental...');
    console.log('📋 conversationId:', conversationId);
    console.log('📋 sessionId:', sessionId);
    console.log('📋 isLoggedIn:', isLoggedIn);

    const headers = {
      'Content-Type': 'application/json',
    };

    if (isLoggedIn) {
      const userId = localStorage.getItem('emoanalyzer_user_id') || '';
      headers['X-User-Id'] = userId;
      headers['X-User-Name'] = localStorage.getItem('emoanalyzer_user_name') || '';
    } else {
      headers['X-Session-Id'] = sessionId || '';
    }

    const adminToken = localStorage.getItem('emoanalyzer_admin_token');
    if (adminToken) headers['X-Admin-Token'] = adminToken;

    const response = await fetch(
      `${API}/conversations/${conversationId}/mental-health-report`,
      { method: 'GET', headers }
      // NOTA: keepalive eliminado porque causa problemas con respuestas grandes (PDFs)
    );

    console.log('📡 Response status:', response.status);

    if (!response.ok) {
      if (response.status === 400) {
        const errorData = await response.json().catch(() => ({}));
        console.log('ℹ️ Conversación muy corta:', errorData.detail);
        throw new Error('CONVERSATION_TOO_SHORT');
      }
      if (response.status === 404) {
        throw new Error('CONVERSATION_NOT_FOUND');
      }
      throw new Error(`HTTP_${response.status}`);
    }

    const blob = await response.blob();
    console.log('📦 Blob recibido:', blob.size, 'bytes, tipo:', blob.type);

    if (blob.size === 0) {
      throw new Error('EMPTY_RESPONSE');
    }

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;

    const now = new Date();
    const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    const sessionShort = (sessionId || 'sesion').slice(0, 8);
    a.download = `informe_clinico_${sessionShort}_${dateStr}.pdf`;

    document.body.appendChild(a);
    a.click();

    setTimeout(() => {
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }, 1000);

    console.log('✅ Informe descargado correctamente');
  } catch (error) {
    console.error('❌ Error al descargar informe:', error);
    throw error; // Re-throw para que handleManualReportDownload lo muestre al usuario
  }
};



// ----------------------------------------

function App() {
  // 🆕 HOOK DE UBICACIÓN
  const location = useLocation();

  // --- Estado de Autenticación y Modo ---
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState(null);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [authTab, setAuthTab] = useState("anonymous");
  const [userEmail, setUserEmail] = useState("");
  const [userPassword, setUserPassword] = useState("");
  
  // 🆕 Estado para captcha inicial
  const [showInitialCaptcha, setShowInitialCaptcha] = useState(true);
  const [initialCaptchaVerified, setInitialCaptchaVerified] = useState(false);

  // --- Estado de Admin ---
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [adminToken, setAdminToken] = useState(null);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");

  // --- Estado de Chat ---
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [loading, setLoading] = useState(false);
  
  // 🆕 Estados de entrenamiento (ahora solo para admin)
  const [showEmotionDialog, setShowEmotionDialog] = useState(false);
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [selectedEmotion, setSelectedEmotion] = useState("");
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const currentConversationRef = useRef(null);
  // --- Estado para admin ---
  const [surveys, setSurveys] = useState([]);
  const [loadingSurveys, setLoadingSurveys] = useState(false);
  const [adminFilter, setAdminFilter] = useState("all");
  
  // 🆕 Estado para controlar sidebar en móvil
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarOpen(prev => !prev);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  // --- Gestión de ID/Headers de Axios con UBICACIÓN ---
  const setAxiosHeaders = (loggedStatus) => {
    const identifier = getUserIdOrSessionId(loggedStatus);
    
    if (loggedStatus) {
      axios.defaults.headers.common['X-User-Id'] = identifier;
      axios.defaults.headers.common['X-User-Name'] = localStorage.getItem('emoanalyzer_user_name') || '';
      delete axios.defaults.headers.common['X-Session-Id'];
    } else {
      axios.defaults.headers.common['X-Session-Id'] = identifier;
      delete axios.defaults.headers.common['X-User-Id'];
      delete axios.defaults.headers.common['X-User-Name'];
    }

    if (!location.loading) {
      axios.defaults.headers.common['X-User-Country'] = location.country;
      axios.defaults.headers.common['X-User-Country-Name'] = location.countryName;
      if (location.city) {
        axios.defaults.headers.common['X-User-City'] = location.city;
      }
      
      console.log('🌍 Headers de ubicación configurados:', {
        country: location.country,
        countryName: location.countryName,
        city: location.city
      });
    }
  };

  // --- EFECTOS DE MONTAJE/ACTUALIZACIÓN ---

  useEffect(() => {
    const storedLogin = localStorage.getItem('emoanalyzer_is_logged_in') === 'true';
    if (storedLogin) {
      setIsLoggedIn(true);
      setUserName(localStorage.getItem('emoanalyzer_user_name') || "Usuario Registrado");
      setShowInitialCaptcha(false);
      setInitialCaptchaVerified(true);
    }
    setAxiosHeaders(storedLogin);
  }, []);

  useEffect(() => {
    if (!location.loading) {
      setAxiosHeaders(isLoggedIn);
    }
  }, [location, isLoggedIn]);

  useEffect(() => {
    if (!isAdminMode) {
      setAxiosHeaders(isLoggedIn);
      loadConversations();
    }
  }, [isLoggedIn, isAdminMode]);

  useEffect(() => {
    if (currentConversation && !isAdminMode) {
      loadMessages(currentConversation.id);
    }
  }, [currentConversation, isAdminMode]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

useEffect(() => {
  currentConversationRef.current = currentConversation;
}, [currentConversation]);

// Y reemplaza el useEffect del beforeunload por este:
useEffect(() => {
  const handleBeforeUnload = async (e) => {
    // Descargar informe si hay conversación activa
    const activeConversation = currentConversationRef.current;
    const sessionId = sessionStorage.getItem('emoanalyzer_session_id');

    if (activeConversation?.id) {
      // Intentar descarga (puede no funcionar en todos los navegadores al cerrar)
      downloadMentalHealthReport(activeConversation.id, sessionId, isLoggedIn);
    }

    // Limpiar sesión anónima
    if (!isLoggedIn && !isAdminMode) {
      cleanupAnonymousSession(isLoggedIn);
    }
  };

  const handlePageHide = (e) => {
    // pagehide es más fiable que beforeunload en móvil/Safari
    const activeConversation = currentConversationRef.current;
    const sessionId = sessionStorage.getItem('emoanalyzer_session_id');

    if (activeConversation?.id) {
      downloadMentalHealthReport(activeConversation.id, sessionId, isLoggedIn);
    }

    if (!isLoggedIn && !isAdminMode) {
      cleanupAnonymousSession(isLoggedIn);
    }
  };

  window.addEventListener('beforeunload', handleBeforeUnload);
  window.addEventListener('pagehide', handlePageHide);

  return () => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
    window.removeEventListener('pagehide', handlePageHide);
  };
}, [isLoggedIn, isAdminMode]);

  const handleInitialCaptchaVerified = (captchaInfo) => {
    setInitialCaptchaVerified(true);
    setShowInitialCaptcha(false);
    setShowAuthDialog(true);
    toast.success("✅ Verificación exitosa");
  };

  const handleInitialCaptchaError = (error) => {
    toast.error("Error en la verificación. Intenta de nuevo.");
  };

  // --- FUNCIONES DE AUTHENTICACIÓN ---

  const handleAnonymousStart = async () => {
    setIsLoggedIn(false);
    setUserName(null);
    localStorage.setItem('emoanalyzer_is_logged_in', 'false');
    localStorage.removeItem('emoanalyzer_user_name');
    setShowAuthDialog(false);
    setAxiosHeaders(false);
    
    try {
      const response = await axios.post(`${API}/conversations`);
      setCurrentConversation(response.data);
      setMessages([]);
    } catch (error) {
      console.error("Error creating initial conversation:", error);
    }
    
    toast.info("🔓 Modo Anónimo: Chat sin historial visible (guardado para análisis).");
  };

  const handleUserLogin = async () => {
    setLoading(true);
    try {
      if (userEmail && userPassword) {
        const displayName = userEmail;
        
        localStorage.setItem('emoanalyzer_is_logged_in', 'true');
        localStorage.setItem('emoanalyzer_user_id', `user_${userEmail.replace(/[^a-z0-9]/gi, '_')}`);
        localStorage.setItem('emoanalyzer_user_name', displayName);
        
        setIsLoggedIn(true);
        setUserName(displayName);
        setShowAuthDialog(false);
        setAxiosHeaders(true);
        setLoading(false);
        toast.success(`¡Bienvenido, ${displayName}! Tus conversaciones se guardarán.`);
        setUserEmail("");
        setUserPassword("");
      } else {
        throw new Error("Credenciales inválidas");
      }
    } catch (error) {
      setLoading(false);
      toast.error("Error de inicio de sesión. Intenta de nuevo.");
    }
  };

  const handleUserLogout = async () => {
    try {
      await axios.delete(`${API}/conversations/cleanup`);
    } catch (error) {
      console.error("Error al limpiar conversaciones:", error);
    }

    setIsLoggedIn(false);
    setUserName(null);
    localStorage.setItem('emoanalyzer_is_logged_in', 'false');
    localStorage.removeItem('emoanalyzer_user_id');
    localStorage.removeItem('emoanalyzer_user_name');
    setCurrentConversation(null);
    setMessages([]);
    setConversations([]);
    setAxiosHeaders(false);
    toast.info("Sesión cerrada. Iniciando modo anónimo.");
  };

  // --- FUNCIONES DE CHAT ---

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadConversations = async () => {
    try {
      const response = await axios.get(`${API}/conversations`);
      setConversations(response.data);
      
      if (response.data.length === 0 && !currentConversation && !isLoggedIn) {
        await createNewConversation();
      } else if (response.data.length > 0 && !currentConversation) {
        setCurrentConversation(response.data[0]);
      } else if (response.data.length === 0) {
        setCurrentConversation(null);
        setMessages([]);
      }
    } catch (error) {
      console.error("Error loading conversations:", error);
      toast.error("Error al cargar conversaciones");
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error("Error loading messages:", error);
      toast.error("Error al cargar mensajes");
    }
  };

  const createNewConversation = async () => {
    try {
      const response = await axios.post(`${API}/conversations`);
      
      if (isLoggedIn) {
        setConversations([response.data, ...conversations]);
      }
      
      setCurrentConversation(response.data);
      setMessages([]);
      
      if (isLoggedIn) {
        toast.success("Nueva conversación creada");
      }
      
      setIsSidebarOpen(false);
    } catch (error) {
      console.error("Error creating conversation:", error);
      toast.error("Error al crear conversación");
    }
  };

  const deleteConversation = async (conversationId) => {
    try {
      await axios.delete(`${API}/conversations/${conversationId}`);
      setConversations(conversations.filter(c => c.id !== conversationId));
      if (currentConversation?.id === conversationId) {
        setCurrentConversation(null);
        setMessages([]);
      }
      toast.success("Conversación eliminada");
    } catch (error) {
      console.error("Error deleting conversation:", error);
      toast.error("Error al eliminar conversación");
    }
  };

  const sendMessage = async () => {
    const messageText = inputText;
    
    if (!messageText.trim() || !currentConversation) return;

    setLoading(true);
    setInputText('');
    
    try {
      const payload = {
        text: messageText
      };

      const response = await axios.post(
        `${API}/conversations/${currentConversation.id}/messages`,
        payload
      );

      await loadMessages(currentConversation.id);
      
      setTimeout(() => inputRef.current?.focus(), 0);

      if (response.data.is_alert) {
        toast.error("⚠️ Alerta: Se detectó contenido de riesgo", {
          duration: 5000,
          description: "Si necesitas ayuda, por favor contacta recursos de apoyo."
        });
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Error al enviar mensaje");
      setInputText(messageText);
      
      setTimeout(() => inputRef.current?.focus(), 0);
    } finally {
      setLoading(false);
    }
  };

const transcribeAndSendAudio = async (audioBlob) => {
  if (!currentConversation) {
    toast.error("Crea una conversación primero");
    return;
  }

  setLoading(true);
  
  try {
    // Convertir audio a base64
    const reader = new FileReader();
    const base64Audio = await new Promise((resolve, reject) => {
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(audioBlob);
    });

    const mimeType = audioBlob.type || 'audio/webm';
    


    // Transcribir
    const transcriptionResponse = await axios.post(
      `${API}/transcribe-audio`,
      {
        audio_base64: base64Audio,
        mime_type: mimeType
      }
    );

    if (!transcriptionResponse.data.success) {
      throw new Error("Error en la transcripción");
    }

    const transcribedText = transcriptionResponse.data.text;
    
    if (!transcribedText || transcribedText.trim() === "") {
      toast.error("No se pudo transcribir el audio. Intenta de nuevo.");
      setLoading(false);
      return;
    }

 
    
    // Enviar como mensaje
    const payload = { text: transcribedText };

    const response = await axios.post(
      `${API}/conversations/${currentConversation.id}/messages`,
      payload
    );

    await loadMessages(currentConversation.id);

    if (response.data.is_alert) {
      toast.error("⚠️ Alerta: Se detectó contenido de riesgo", {
        duration: 5000,
        description: "Si necesitas ayuda, por favor contacta recursos de apoyo."
      });
    }

  } catch (error) {
    console.error("Error processing audio:", error);
    
    if (error.response?.data?.detail) {
      toast.error(`Error: ${error.response.data.detail}`);
    } else {
      toast.error("Error al procesar el audio. Intenta de nuevo.");
    }
  } finally {
    setLoading(false);
  }
};
  // 🆕 Funciones de entrenamiento (ahora solo usadas en admin)
  const handleTrainEmotion = async () => {
    if (!selectedEmotion || !selectedMessage) return;

    try {
      await axios.post(`${API}/training/emotion`, {
        message_id: selectedMessage.id,
        emotion: selectedEmotion
      });
      toast.success("Emoción entrenada correctamente");
      setShowEmotionDialog(false);
      
      // Recargar las encuestas en modo admin
      if (isAdminMode) {
        await loadSurveys();
      }
    } catch (error) {
      console.error("Error training emotion:", error);
      toast.error("Error al entrenar emoción");
    }
  };

  const handleTrainAlert = async (isAlert) => {
    if (!selectedMessage) return;

    try {
      await axios.post(`${API}/training/alert`, {
        message_id: selectedMessage.id,
        is_alert: isAlert
      });
      toast.success(isAlert ? "Marcado como alerta" : "Desmarcado como alerta");
      setShowAlertDialog(false);
      
      // Recargar las encuestas en modo admin
      if (isAdminMode) {
        await loadSurveys();
      }
    } catch (error) {
      console.error("Error training alert:", error);
      toast.error("Error al entrenar alerta");
    }
  };

  const openEmotionDialog = (message) => {
    setSelectedMessage(message);
    setSelectedEmotion(message.emotion || "");
    setShowEmotionDialog(true);
  };

  const openAlertDialog = (message) => {
    setSelectedMessage(message);
    setShowAlertDialog(true);
  };

  const getEmotionColor = (emotion) => {
    const colors = {
      'alegría': 'from-emerald-400/20 to-teal-400/20 border-emerald-400/40',
      'tristeza': 'from-blue-400/20 to-indigo-400/20 border-blue-400/40',
      'neutral': 'from-gray-400/20 to-slate-400/20 border-gray-400/40',
      'enojo': 'from-red-400/20 to-orange-400/20 border-red-400/40',
      'miedo': 'from-purple-400/20 to-pink-400/20 border-purple-400/40'
    };
    return colors[emotion] || colors['neutral'];
  };

  // --- FUNCIONES ADMIN ---

  const handleAdminLogin = async () => {
    try {
      const response = await axios.post(`${API}/admin/login`, {
        password: adminPassword
      });
      
      if (response.data.success) {
        setAdminToken(response.data.token);
        axios.defaults.headers.common['X-Admin-Token'] = response.data.token;
        setIsAdminMode(true);
        setShowAdminLogin(false);
        setAdminPassword("");
        toast.success("¡Bienvenido Administrador!");
        loadSurveys();
      }
    } catch (error) {
      console.error("Error login admin:", error);
      toast.error("Contraseña incorrecta");
      setAdminPassword("");
    }
  };

  const handleAdminLogout = () => {
    setIsAdminMode(false);
    setAdminToken(null);
    delete axios.defaults.headers.common['X-Admin-Token'];
    setSurveys([]);
    toast.info("Sesión admin cerrada");
  };

  const loadSurveys = async () => {
    setLoadingSurveys(true);
    try {
      const response = await axios.get(`${API}/admin/surveys`);
      
      setSurveys(response.data.surveys || []);
      toast.success(`${response.data.surveys?.length || 0} conversaciones cargadas`);
    } catch (error) {
      console.error("Error loading surveys:", error);
      toast.error("Error al cargar encuestas");
      setSurveys([]);
    } finally {
      setLoadingSurveys(false);
    }
  };

  const getFilteredSurveys = () => {
    switch (adminFilter) {
      case "alerts":
        return surveys.filter(s => s.has_alerts);
      case "no-alerts":
        return surveys.filter(s => !s.has_alerts);
      default:
        return surveys;
    }
  };

  const filteredSurveys = getFilteredSurveys();
const handleManualReportDownload = async () => {
  if (!currentConversation?.id) {
    toast.error("No hay conversación activa");
    return;
  }

  const sessionId = sessionStorage.getItem('emoanalyzer_session_id');

  // Verificar que haya mensajes antes de intentar
  if (messages.length === 0) {
    toast.error("Escribe al menos un mensaje antes de generar el informe");
    return;
  }

  const userMessageCount = messages.filter(m => m.is_user).length;
  if (userMessageCount === 0) {
    toast.error("Escribe al menos un mensaje antes de generar el informe");
    return;
  }

  const toastId = toast.loading("📋 Generando informe clínico...");

  try {
    await downloadMentalHealthReport(currentConversation.id, sessionId, isLoggedIn);
    toast.dismiss(toastId);
    toast.success("✅ Informe descargado correctamente");
  } catch (e) {
    toast.dismiss(toastId);
    console.error("❌ Error completo:", e);

    if (e.message === 'CONVERSATION_TOO_SHORT') {
      toast.error("La conversación es demasiado corta. Escribe más mensajes primero.");
    } else if (e.message === 'CONVERSATION_NOT_FOUND') {
      toast.error("No se encontró la conversación. Intenta recargar la página.");
    } else if (e.message === 'EMPTY_RESPONSE') {
      toast.error("El servidor devolvió un PDF vacío. Revisa el backend.");
    } else {
      toast.error(`Error al generar el informe: ${e.message}`);
    }
  }
};
  // RENDER: Modo Admin
  if (isAdminMode) {
    return (
      <div className="App admin-mode" data-testid="admin-panel">
        <Toaster position="top-center" richColors />
        
        <div className="admin-container">
          <div className="admin-header">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-teal-400" />
              <div>
                <h1 className="text-2xl font-bold text-white">Panel de Administración</h1>
                <p className="text-gray-400 text-sm">Vista de todas las conversaciones y encuestas</p>
              </div>
            </div>
            <Button
              onClick={handleAdminLogout}
              variant="outline"
              className="gap-2"
            >
              <LogOut className="w-4 h-4" />
              Cerrar Sesión
            </Button>
          </div>

          <div className="admin-content">
            <div className="flex flex-col gap-4 mb-6">
              <div className="flex flex-wrap gap-4">
                <Card
                  className={`bg-gray-800/50 border-gray-700 cursor-pointer transition-all ${
                    adminFilter === "all" ? "ring-2 ring-teal-400" : ""
                  }`}
                  onClick={() => setAdminFilter("all")}
                >
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-gray-400">Total Conversaciones</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-white">{surveys.length}</p>
                  </CardContent>
                </Card>
                
                <Card
                  className={`bg-gray-800/50 border-gray-700 cursor-pointer transition-all ${
                    adminFilter === "alerts" ? "ring-2 ring-red-400" : ""
                  }`}
                  onClick={() => setAdminFilter("alerts")}
                >
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-gray-400">Con Alertas</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-red-400">
                      {surveys.filter(s => s.has_alerts).length}
                    </p>
                  </CardContent>
                </Card>

                <Card
                  className={`bg-gray-800/50 border-gray-700 cursor-pointer transition-all ${
                    adminFilter === "no-alerts" ? "ring-2 ring-green-400" : ""
                  }`}
                  onClick={() => setAdminFilter("no-alerts")}
                >
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-gray-400">Sin Alertas</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-green-400">
                      {surveys.filter(s => !s.has_alerts).length}
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-gray-800/50 border-gray-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-gray-400">Total Mensajes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-teal-400">
                      {surveys.reduce((sum, s) => sum + s.message_count, 0)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="flex justify-between items-center">
                <div className="flex gap-2">
                  <Badge variant={adminFilter === "all" ? "default" : "outline"}>
                    Mostrando: {filteredSurveys.length} conversaciones
                  </Badge>
                  {adminFilter !== "all" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setAdminFilter("all")}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Limpiar filtro
                    </Button>
                  )}
                </div>
                <Button onClick={loadSurveys} disabled={loadingSurveys}>
                  <BarChart3 className="w-4 h-4 mr-2" />
                  {loadingSurveys ? "Cargando..." : "Recargar"}
                </Button>
              </div>
            </div>

            <ScrollArea className="h-[calc(100vh-320px)]">
              <div className="space-y-4">
                {filteredSurveys.map((survey, idx) => {
                  const userInfo = survey.conversation.user_info;
                  const displayName = userInfo?.name || (userInfo?.is_anonymous ? 'Usuario Anónimo' : `Conversación #${idx + 1}`);
                  const displayId = userInfo?.id || survey.conversation.id;

                  const displayMessages = adminFilter === "alerts"
                    ? survey.messages.filter(msg => msg.is_alert)
                    : survey.messages;

                  return (
                    <Card key={survey.conversation.id} className="bg-gray-800/50 border-gray-700">
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-white flex items-center gap-2">
                              {displayName}
                              {survey.has_alerts && (
                                <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full flex items-center gap-1">
                                  <AlertTriangle className="w-3 h-3" />
                                  Contiene alertas
                                </span>
                              )}
                            </CardTitle>
                            <CardDescription>
                              ID: {displayId} • {new Date(survey.conversation.created_at).toLocaleString('es-ES')} • {survey.message_count} mensajes
                              {adminFilter === "alerts" && (
                                <span className="ml-2 text-red-400">
                                  ({displayMessages.length} alertas)
                                </span>
                              )}
                            </CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {displayMessages.map((msg, msgIdx) => (
                            <div
                              key={msgIdx}
                              className={`p-3 rounded-lg ${
                                msg.is_user
                                  ? 'bg-teal-500/10 border border-teal-500/30'
                                  : 'bg-gray-700/50 border border-gray-600/30'
                              } ${
                                msg.is_alert ? 'ring-2 ring-red-400/50' : ''
                              }`}
                            >
                              <div className="flex justify-between items-start mb-1">
                                <span className="text-xs font-semibold text-gray-400">
                                  {msg.is_user ? '👤 Usuario' : '🤖 Asistente'}
                                </span>
                                <div className="flex items-center gap-2">
                                  {msg.emotion && (
                                    <span className="text-xs px-2 py-0.5 bg-gray-600/50 rounded-full text-gray-300">
                                      {msg.emotion}
                                    </span>
                                  )}
                                  {/* 🆕 BOTONES DE ENTRENAMIENTO SOLO EN ADMIN */}
                                  {msg.is_user && (
                                    <div className="flex gap-1">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6"
                                        onClick={() => openEmotionDialog(msg)}
                                        title="Entrenar emoción"
                                      >
                                        <Tag className="w-3 h-3" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6"
                                        onClick={() => openAlertDialog(msg)}
                                        title="Entrenar alerta"
                                      >
                                        <AlertTriangle className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <p className="text-white text-sm">{msg.text}</p>
                              {msg.is_alert && (
                                <div className="mt-2 flex items-center gap-1 text-red-400 text-xs font-semibold">
                                  <AlertTriangle className="w-3 h-3" />
                                  Mensaje marcado como alerta
                                </div>
                              )}
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(msg.timestamp).toLocaleTimeString('es-ES')}
                              </p>
                            </div>
                          ))}
                          
                          {displayMessages.length === 0 && adminFilter === "alerts" && (
                            <div className="text-center py-4 text-gray-400 text-sm">
                              No hay mensajes de alerta en esta conversación
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}

                {filteredSurveys.length === 0 && !loadingSurveys && (
                  <div className="text-center py-12 text-gray-400">
                    <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p>
                      {adminFilter === "alerts"
                        ? "No hay conversaciones con alertas"
                        : adminFilter === "no-alerts"
                        ? "No hay conversaciones sin alertas"
                        : "No hay conversaciones registradas"}
                    </p>
                  </div>
                )}

                {loadingSurveys && (
                  <div className="text-center py-12 text-gray-400">
                    <BarChart3 className="w-16 h-16 mx-auto mb-4 opacity-30 animate-pulse" />
                    <p>Cargando conversaciones...</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>

        {/* 🆕 DIÁLOGOS DE ENTRENAMIENTO (ahora solo en admin) */}
        <Dialog open={showEmotionDialog} onOpenChange={setShowEmotionDialog}>
          <DialogContent data-testid="emotion-dialog" className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Entrenar Emoción</DialogTitle>
              <DialogDescription>
                Selecciona la emoción correcta para este mensaje
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-300 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                {selectedMessage?.text}
              </p>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-300">Emoción</label>
                <select
                  data-testid="emotion-select"
                  value={selectedEmotion}
                  onChange={(e) => setSelectedEmotion(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="" disabled>Selecciona una emoción</option>
                  <option value="alegría">😊 Alegría</option>
                  <option value="tristeza">😢 Tristeza</option>
                  <option value="enojo">😠 Enojo</option>
                  <option value="miedo">😰 Miedo</option>
                  <option value="preocupación">😟 Preocupación</option>
                  <option value="neutral">😐 Neutral</option>
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button
                data-testid="save-emotion-btn"
                onClick={handleTrainEmotion}
                disabled={!selectedEmotion}
                className="w-full sm:w-auto"
              >
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showAlertDialog} onOpenChange={setShowAlertDialog}>
          <DialogContent data-testid="alert-dialog">
            <DialogHeader>
              <DialogTitle>Entrenar Alerta</DialogTitle>
              <DialogDescription>
                ¿Este mensaje debe considerarse una alerta?
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-300">{selectedMessage?.text}</p>
            </div>
            <DialogFooter className="gap-2">
              <Button
                data-testid="mark-as-alert-btn"
                onClick={() => handleTrainAlert(true)}
                variant="destructive"
              >
                Sí, es alerta
              </Button>
              <Button
                data-testid="mark-as-not-alert-btn"
                onClick={() => handleTrainAlert(false)}
                variant="outline"
              >
                No es alerta
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // RENDER: Modo Usuario Normal / Anónimo
  return (
    <div className="App" data-testid="emotion-analyzer-app">
      <Toaster position="top-center" richColors />
      
      <div className="fixed top-4 right-4 z-50 flex gap-2 items-center">
  {!location.loading && (
    <Badge variant="outline" className="bg-gray-800/80 text-gray-300 border-gray-600">
      <MapPin className="w-3 h-3 mr-1" />
      {location.countryName}
    </Badge>
  )}

  {/* BOTÓN INFORME - dentro del div */}
  <Button
    onClick={handleManualReportDownload}
    className="bg-teal-800/80 hover:bg-teal-700"
    size="sm"
    title="Descargar Informe Clínico"
    disabled={!currentConversation}
  >
    <FileDown className="w-4 h-4" />
  </Button>

  {isLoggedIn && (
    <Button
      onClick={handleUserLogout}
      className="bg-red-800/80 hover:bg-red-700"
      size="sm"
      variant="destructive"
      title="Cerrar Sesión"
    >
      <LogOut className="w-4 h-4" />
    </Button>
  )}
  <Button
    onClick={() => setShowAdminLogin(true)}
    className="bg-gray-800/80 hover:bg-gray-700"
    size="sm"
    title="Acceso Admin"
  >
    <Lock className="w-4 h-4" />
  </Button>
</div>
        
      {isSidebarOpen && (
        <div
          className="sidebar-overlay active"
          onClick={closeSidebar}
          data-testid="sidebar-overlay"
        />
      )}
      
      <div className="app-container">
        <div
          className={`sidebar ${isSidebarOpen ? 'active-mobile' : ''}`}
          data-testid="conversations-sidebar"
        >
          <div className="sidebar-header">
            <h2 className="sidebar-title">
              {isLoggedIn ? "Tus Conversaciones" : "Conversaciones Anónimas"}
            </h2>
            <div className="flex gap-2">
              <Button
                data-testid="new-conversation-btn"
                onClick={createNewConversation}
                size="sm"
                className="new-chat-btn"
              >
                <Plus className="w-4 h-4" />
              </Button>
              <Button
                onClick={closeSidebar}
                size="sm"
                variant="ghost"
                className="md:hidden text-gray-400 hover:text-white"
                title="Cerrar"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          <ScrollArea className="conversation-list">
            {isLoggedIn ? (
              <>
                {conversations.map((conv) => (
                  <div
                    key={conv.id}
                    data-testid={`conversation-${conv.id}`}
                    className={`conversation-item ${
                      currentConversation?.id === conv.id ? "active" : ""
                    }`}
                    onClick={() => {
                      setCurrentConversation(conv);
                      closeSidebar();
                    }}
                  >
                    <div className="conversation-content">
                      <span className="conversation-title">{conv.title}</span>
                      <span className="conversation-date">
                        {new Date(conv.updated_at).toLocaleDateString()}
                      </span>
                    </div>
                    <Button
                      data-testid={`delete-conversation-${conv.id}`}
                      variant="ghost"
                      size="sm"
                      className="delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteConversation(conv.id);
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                {conversations.length === 0 && (
                  <div className="text-center p-4 text-gray-500">
                    Aún no tienes conversaciones. ¡Crea una!
                  </div>
                )}
              </>
            ) : (
              <div className="text-center p-4 text-gray-400 space-y-2">
                <EyeOff className="w-12 h-12 mx-auto opacity-50" />
                <p className="text-sm font-medium">Modo Anónimo</p>
                <p className="text-xs">
                  No se muestra historial de conversaciones.
                  Los datos se guardan para análisis estadístico.
                </p>
              </div>
            )}
          </ScrollArea>
        </div>

        <div className="chat-area" data-testid="chat-area">
          {currentConversation ? (
            <>
              <div className="chat-header">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleSidebar}
                  className="md:hidden absolute left-3 top-1/2 -translate-y-1/2 z-10"
                  title="Abrir conversaciones"
                >
                  <Menu className="w-6 h-6 text-teal-400" />
                </Button>
                
                <div className="header-content md:ml-0 ml-12">
                  <Brain className="w-6 h-6 text-teal-400" />
                  <h1 className="chat-title">Analizador de Emociones</h1>
                </div>
                <p className="chat-subtitle md:ml-12">
                  Conversación empática con detección de emociones.
                  <span className="font-semibold ml-2 text-sm text-teal-400">
                    {isLoggedIn ? `[Sesión: ${userName}]` : `[Sesión: Anónima - Sin historial visible]`}
                  </span>
                </p>
              </div>

              <ScrollArea className="messages-container" data-testid="messages-container">
                <div className="messages-wrapper">
                  {messages.map((message, index) => (
  <div
    key={message.id}
    data-testid={`message-${index}`}
    className={`message-wrapper ${
      message.is_user ? "user-message" : "ai-message"
    }`}
  >
    <div
      className={`message-bubble ${
        message.is_user
          ? `user ${getEmotionColor(message.emotion)}`
          : "assistant"
      } ${
        message.is_alert ? "alert-message" : ""
      }`}
    >
      {message.is_alert && (
        <div className="alert-badge" data-testid="alert-badge">
          <AlertTriangle className="w-4 h-4" />
          <span>Alerta</span>
        </div>
      )}
      <p className="message-text">
  {!message.is_user && message.observacion_clinica
    ? "Orientación clínica generada ✓"  // o lo que quieras mostrar al usuario
    : message.text
  }
</p>
    </div>
{/* ── Indicador de salud mental (solo en mensajes del usuario) ── */}
{message.is_user && (
  <MentalHealthIndicator
    message={message}
    aiMessage={
      messages[index + 1] && !messages[index + 1].is_user
        ? messages[index + 1]
        : null
    }
  />
)}
  </div>
))}
                  
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              <div className="input-area" data-testid="input-area">
  <div className="input-container">
    <Input
      ref={inputRef}
      data-testid="message-input"
      type="text"
      placeholder="Escribe tu mensaje o graba un audio..."
      value={inputText}
      onChange={(e) => setInputText(e.target.value)}
      onKeyPress={(e) => e.key === "Enter" && !loading && sendMessage()}
      disabled={loading}
      className="message-input"
    />
    
    <div className="flex items-center gap-2">
      <AudioRecorder 
        onAudioSend={transcribeAndSendAudio}
        disabled={loading}
      />
      
      <Button
        data-testid="send-message-btn"
        onClick={() => sendMessage()}
        disabled={loading || !inputText.trim()}
        className="send-btn"
      >
        <Send className="w-5 h-5" />
      </Button>
    </div>
  </div>
  <p className="disclaimer-text">
    Puedo cometer errores. Verifica la información.
  </p>
</div>
            </>
          ) : (
            <div className="empty-state" data-testid="empty-state">
              <Brain className="w-20 h-20 text-teal-400/30" />
              <p className="empty-text">Crea una nueva conversación para comenzar</p>
            </div>
          )}
        </div>
      </div>

      <Dialog open={showInitialCaptcha} onOpenChange={() => {}} modal={true}>
        <DialogContent data-testid="initial-captcha-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Verificación de Seguridad</DialogTitle>
            <DialogDescription>
              Para continuar, por favor completa esta verificación rápida
            </DialogDescription>
          </DialogHeader>
          <CustomCaptcha
            key={showInitialCaptcha ? Date.now() : 'initial-captcha'}
            onVerified={handleInitialCaptchaVerified}
            onError={handleInitialCaptchaError}
            isOpen={showInitialCaptcha}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={showAuthDialog && initialCaptchaVerified} onOpenChange={setShowAuthDialog} modal={!isLoggedIn}>
        <DialogContent data-testid="auth-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Bienvenido a AffectiveIA
            </DialogTitle>
            <DialogDescription>
              Elige cómo deseas continuar. Las conversaciones anónimas no se mostrarán en el historial.
            </DialogDescription>
          </DialogHeader>

          <Tabs value={authTab} onValueChange={setAuthTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login" data-testid="login-tab-btn">
                <User className="w-4 h-4 mr-2" /> Iniciar Sesión
              </TabsTrigger>
              <TabsTrigger value="anonymous" data-testid="anonymous-tab-btn">
                <EyeOff className="w-4 h-4 mr-2" /> Anónimo
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-4 space-y-4">
              <Input
                type="email"
                placeholder="Correo Electrónico"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
              />
              <Input
                type="password"
                placeholder="Contraseña"
                value={userPassword}
                onChange={(e) => setUserPassword(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && !loading && handleUserLogin()}
              />
            </TabsContent>

            <TabsContent value="anonymous" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Modo Anónimo</CardTitle>
                  <CardDescription>
                    Tus conversaciones no se mostrarán en el historial pero se guardarán de forma anónima para análisis estadístico y mejora del servicio.
                  </CardDescription>
                </CardHeader>
              </Card>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            {authTab === "login" ? (
              <Button
                onClick={handleUserLogin}
                disabled={!userEmail || !userPassword || loading}
                data-testid="login-submit-btn"
              >
                {loading ? "Iniciando..." : "Ingresar"}
              </Button>
            ) : (
              <Button
                onClick={handleAnonymousStart}
                variant="outline"
                data-testid="anonymous-start-btn"
              >
                Usar de forma Anónima
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Dialog open={showAdminLogin} onOpenChange={setShowAdminLogin}>
        <DialogContent data-testid="admin-login-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Acceso Administrador
            </DialogTitle>
            <DialogDescription>
              Ingresa la contraseña para acceder al panel de administración
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              type="password"
              placeholder="Contraseña"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleAdminLogin()}
              data-testid="admin-password-input"
            />
          </div>
          <DialogFooter>
            <Button
              onClick={handleAdminLogin}
              disabled={!adminPassword}
              data-testid="admin-login-btn"
            >
              Iniciar Sesión
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default App;
